export type HlsRecoveryState = {
  mediaRecoveryAttempted: boolean;
  stallRecoveryAttempted: boolean;
};

export type HlsRecoveryInput = {
  fatal: boolean;
  type: string;
  details: string;
  httpStatus?: number;
  bufferedAheadSeconds: number;
  paused: boolean;
  ended: boolean;
};

export type HlsRecoveryDecision =
  | { action: "ignore"; showBuffering: boolean }
  | { action: "restart-load"; showBuffering: true }
  | { action: "recover-media"; showBuffering: boolean }
  | {
      action: "fail";
      kind: "session-gone" | "network" | "media" | "unknown";
      message: string;
    };

// Remux holds a pending child playlist for at most 15 seconds. The Web loader
// must leave enough headroom to receive and classify its final 410/503 instead
// of aborting the request at the same instant.
export const HLS_SERVER_PLAYLIST_READINESS_MS = 15_000;
export const HLS_PLAYLIST_TTFB_TIMEOUT_MS = 18_000;

export function shouldDeferHlsFailure(input: {
  paused: boolean;
  ended: boolean;
  hasBufferedMedia: boolean;
}) {
  return input.paused && !input.ended && input.hasBufferedMedia;
}

/**
 * hls.js already applies its configured loader retry policy. This classifier
 * handles only the boundary after those retries, plus one decoder recovery.
 * It deliberately never calls startLoad for a fatal request: doing so resets
 * the loader and can retry one permanently missing fragment forever.
 */
export function decideHlsRecovery(
  input: HlsRecoveryInput,
  state: HlsRecoveryState,
): HlsRecoveryDecision {
  const genuinelyStalled =
    !input.paused &&
    !input.ended &&
    input.bufferedAheadSeconds < 0.5;

  if (!input.fatal) {
    if (
      input.details === "bufferStalledError" &&
      genuinelyStalled &&
      !state.stallRecoveryAttempted
    ) {
      return { action: "restart-load", showBuffering: true };
    }
    return {
      action: "ignore",
      showBuffering: input.details === "bufferStalledError" && genuinelyStalled,
    };
  }

  if (input.type === "mediaError" && !state.mediaRecoveryAttempted) {
    return { action: "recover-media", showBuffering: genuinelyStalled };
  }

  if (input.type === "networkError") {
    const sessionGone = input.httpStatus === 410;
    return {
      action: "fail",
      kind: sessionGone ? "session-gone" : "network",
      message: sessionGone
        ? "The playback generation expired."
        : "The HLS stream could not load required data.",
    };
  }

  if (input.type === "mediaError") {
    return {
      action: "fail",
      kind: "media",
      message: "The decoder could not recover this HLS stream.",
    };
  }

  return {
    action: "fail",
    kind: "unknown",
    message: "The HLS stream failed.",
  };
}

export function decidePlaybackGenerationFailure(input: {
  retryableGenerationFailure: boolean;
  sameSourceRecoveryUsed: boolean;
  sourceHasPlayed: boolean;
  automaticSourceRecoveryEnabled: boolean;
}): "regenerate-source" | "fail-for-user" | "source-fallback" {
  if (
    input.retryableGenerationFailure &&
    !input.sameSourceRecoveryUsed &&
    input.sourceHasPlayed
  ) {
    return "regenerate-source";
  }
  return input.sourceHasPlayed && !input.automaticSourceRecoveryEnabled
    ? "fail-for-user"
    : "source-fallback";
}
